import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, IconButton, Grid, Paper, TextField, Button,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Snackbar, Tooltip, Chip,
  List, ListItem, ListItemText, CircularProgress,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { DataGrid, esES } from '@material-ui/data-grid';
import * as Icons from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import NoRowsOverlay from '../components/NoRowsOverlay';
import QuickSearchToolbar from '../components/QuickSearchToolbar';
import moment from 'moment';
import { invokeApig } from '../libs/awsLib';


const useStyles = makeStyles((theme) => ({
  section: {
    margin: theme.spacing(3, 0),
    marginBottom: theme.spacing(10),
    width: 'inherit',
  },
  paper: {
    padding: theme.spacing(2),
  },
  gridMaxMd: {
    [theme.breakpoints.up('md')]: {
      maxWidth: theme.breakpoints.values.lg,
    },
  },
  deleteButton: {
    backgroundColor: '#f44336',
    '&:hover': {
      backgroundColor: '#f44336',
    },
  },
  textValidate: {
    margin: '15px 0',
  },
  editIcon: {
    color: '#2196f3',
  },
  deleteIcon: {
    color: '#f44336',
  },
  editorIcon: {
    color: '#9c27b0',
  },
  guidedIcon: {
    color: '#f57c00',
  },
  contentIcon: {
    color: '#00796b',
  },
  linkIcon: {
    color: '#009688',
  },
  buttonsDialogContainer: {
    padding: '10px 25px',
  },
}));


function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

const STATUS_COLORS = {
  DRAFT:  '#ff9800',
  ACTIVE: '#4caf50',
};

function DesignsPage() {
  const classes = useStyles();
  const history = useHistory();

  const [isLoading,  setIsLoading]  = useState(false);
  const [isError,    setIsError]    = useState(false);
  const [message,    setMessage]    = useState('');
  const [openToast,  setOpenToast]  = useState(false);
  const [toastMsg,   setToastMsg]   = useState('');

  const [designs,    setDesigns]    = useState([]);
  const [rows,       setRows]       = useState([]);
  const [searchText, setSearchText] = useState('');

  // ── Crear ──────────────────────────────────────────────────────────────────
  const [openCreate,  setOpenCreate]  = useState(false);
  const [createData,  setCreateData]  = useState({ name: '', description: '' });
  const [isSaving,    setIsSaving]    = useState(false);

  // ── Editar ─────────────────────────────────────────────────────────────────
  const [openEdit,    setOpenEdit]    = useState(false);
  const [editData,    setEditData]    = useState({ name: '', description: '' });
  const [editId,      setEditId]      = useState('');

  // ── Eliminar ───────────────────────────────────────────────────────────────
  const [openDelete,   setOpenDelete]   = useState(false);
  const [deleteId,     setDeleteId]     = useState('');
  const [textValidate, setTextValidate] = useState('');
  const [isDeleting,   setIsDeleting]   = useState(false);

  // ── Asociar dominio ────────────────────────────────────────────────────────
  const [openAssign,     setOpenAssign]     = useState(false);
  const [assignDesignId, setAssignDesignId] = useState('');
  const [sites,          setSites]          = useState([]);
  const [assignSiteId,   setAssignSiteId]   = useState('');
  const [isAssigning,    setIsAssigning]    = useState(false);
  const [isSiteLoading,  setIsSiteLoading]  = useState(false);


  useEffect(() => {
    fetchDesigns();
  }, []);


  // ── API ────────────────────────────────────────────────────────────────────

  const fetchDesigns = async () => {
    try {
      setIsLoading(true);
      setIsError(false);
      setDesigns([]);
      setRows([]);
      setSearchText('');
      const response = await invokeApig({ path: '/design/list', method: 'POST', body: {} });
      if (response.Status === 0 || response.statusCode === 200) {
        setDesigns(response.designs || []);
        setRows(response.designs || []);
      } else {
        setMessage('Error al obtener los diseños. Por favor contactar a Soporte.');
        setIsError(true);
      }
    } catch (error) {
      setMessage('Error al obtener los diseños. Intenta nuevamente o consulta a Soporte.');
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const createDesign = async () => {
    if (!createData.name.trim()) return;
    try {
      setIsSaving(true);
      setIsError(false);
      const response = await invokeApig({
        path:   '/design',
        method: 'POST',
        body:   { name: createData.name.trim(), description: createData.description.trim() },
      });
      if (response.Status === 0 || response.statusCode === 200) {
        setToastMsg('Diseño creado correctamente');
        setOpenToast(true);
        setOpenCreate(false);
        setCreateData({ name: '', description: '' });
        fetchDesigns();
      } else {
        setMessage(response.message || 'Error al crear el diseño');
        setIsError(true);
      }
    } catch (error) {
      setMessage('Ocurrió un error al crear el diseño');
      setIsError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const updateDesign = async () => {
    if (!editData.name.trim()) return;
    try {
      setIsSaving(true);
      setIsError(false);
      const response = await invokeApig({
        path:   '/design',
        method: 'PUT',
        body:   { designId: editId, name: editData.name.trim(), description: editData.description.trim() },
      });
      if (response.Status === 0 || response.statusCode === 200) {
        setToastMsg('Diseño actualizado correctamente');
        setOpenToast(true);
        setOpenEdit(false);
        fetchDesigns();
      } else {
        setMessage(response.message || 'Error al actualizar el diseño');
        setIsError(true);
      }
    } catch (error) {
      setMessage('Ocurrió un error al actualizar el diseño');
      setIsError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteDesign = async () => {
    try {
      setIsDeleting(true);
      setIsError(false);
      const response = await invokeApig({
        path:   '/design',
        method: 'DELETE',
        body:   { designId: deleteId },
      });
      if (response.Status === 0 || response.statusCode === 200) {
        setToastMsg('Diseño eliminado correctamente');
        setOpenToast(true);
        setOpenDelete(false);
        setTextValidate('');
        fetchDesigns();
      } else {
        setMessage(response.message || 'Error al eliminar el diseño');
        setIsError(true);
      }
    } catch (error) {
      setMessage('Ocurrió un error al eliminar el diseño');
      setIsError(true);
    } finally {
      setIsDeleting(false);
    }
  };


  // ── Handlers ───────────────────────────────────────────────────────────────

  const requestSearch = (value) => {
    setSearchText(value);
    const regex = new RegExp(escapeRegExp(value), 'i');
    setRows(designs.filter((row) =>
      Object.values(row).some((v) => v && regex.test(v.toString()))
    ));
  };

  const handleOpenEdit = (row) => {
    setEditId(row.designId);
    setEditData({ name: row.name, description: row.description || '' });
    setIsError(false);
    setOpenEdit(true);
  };

  const handleOpenDelete = (designId) => {
    setDeleteId(designId);
    setTextValidate('');
    setIsError(false);
    setOpenDelete(true);
  };

  const handleOpenEditor = (designId) => {
    history.push(`/editor/${designId}`);
  };

  const handleOpenGuidedDesigner = (designId) => {
    history.push(`/designer/guided/${designId}`);
  };

  const handleOpenContentManager = (designId) => {
    history.push(`/designer/content/${designId}`);
  };

  const fetchSites = async () => {
    try {
      setIsSiteLoading(true);
      const response = await invokeApig({ path: '/site/find', method: 'POST', body: {} });
      if (response.statusCode === 200) {
        setSites((response.sites || []).filter((s) => s.status === 'ACTIVE'));
      }
    } catch (error) {
      console.error('Error cargando sitios:', error);
    } finally {
      setIsSiteLoading(false);
    }
  };

  const assignDesign = async () => {
    try {
      setIsAssigning(true);
      setIsError(false);
      const response = await invokeApig({
        path:   '/site/assign-design',
        method: 'PUT',
        body:   { domain: assignSiteId, designId: assignDesignId },
      });
      if (response.Status === 0 || response.statusCode === 200) {
        setToastMsg('Diseño asociado al dominio correctamente');
        setOpenToast(true);
        setOpenAssign(false);
      } else {
        setMessage(response.message || 'Error al asociar el diseño');
        setIsError(true);
      }
    } catch (error) {
      setMessage('Ocurrió un error al asociar el diseño');
      setIsError(true);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleOpenAssign = (designId) => {
    setAssignDesignId(designId);
    setAssignSiteId('');
    setIsError(false);
    setOpenAssign(true);
    fetchSites();
  };


  // ── Columnas ───────────────────────────────────────────────────────────────

  const columns = [
    {
      field: 'updatedAt',
      headerName: 'Actualizado',
      flex: 1,
      filterable: false,
      renderCell: (params) => params.value ? moment(params.value).format('DD-MM-YYYY HH:mm') : '—',
    },
    {
      field: 'name',
      headerName: 'Nombre',
      flex: 1.5,
    },
    {
      field: 'description',
      headerName: 'Descripción',
      flex: 2,
    },
    {
      field: 'domain',
      headerName: 'Dominio',
      flex: 1.5,
      renderCell: (params) => params.value || <span style={{ color: '#aaa' }}>Sin dominio</span>,
    },
    {
      field: 'status',
      headerName: 'Estado',
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          style={{
            color: '#fff',
            backgroundColor: STATUS_COLORS[params.value] || '#9e9e9e',
          }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: 2,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <>
          <Tooltip title="Editor Avanzado" enterDelay={500} leaveDelay={200}>
            <IconButton onClick={() => handleOpenEditor(params.row.designId)} className={classes.editorIcon}>
              <Icons.Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Diseño Guiado" enterDelay={500} leaveDelay={200}>
            <IconButton onClick={() => handleOpenGuidedDesigner(params.row.designId)} className={classes.guidedIcon}>
              <Icons.Dashboard />
            </IconButton>
          </Tooltip>
          <Tooltip title="Gestionar Contenido" enterDelay={500} leaveDelay={200}>
            <IconButton onClick={() => handleOpenContentManager(params.row.designId)} className={classes.contentIcon}>
              <Icons.LibraryBooks />
            </IconButton>
          </Tooltip>
          <Tooltip title="Asociar Dominio" enterDelay={500} leaveDelay={200}>
            <IconButton onClick={() => handleOpenAssign(params.row.designId)} className={classes.linkIcon}>
              <Icons.Link />
            </IconButton>
          </Tooltip>
          <Tooltip title="Renombrar" enterDelay={500} leaveDelay={200}>
            <IconButton onClick={() => handleOpenEdit(params.row)} className={classes.editIcon}>
              <Icons.TextFields />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar Diseño" enterDelay={500} leaveDelay={200}>
            <IconButton onClick={() => handleOpenDelete(params.row.designId)} className={classes.deleteIcon}>
              <Icons.Delete />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];


  // ── Diálogos ───────────────────────────────────────────────────────────────

  const createDialog = (
    <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="sm">
      <DialogTitle>Nuevo Diseño</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: 4 }}>
          <Grid item xs={12}>
            <TextField
              label="Nombre del diseño"
              name="name"
              fullWidth
              autoFocus
              value={createData.name}
              onChange={(e) => setCreateData((d) => ({ ...d, name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descripción (opcional)"
              name="description"
              fullWidth
              multiline
              rows={3}
              value={createData.description}
              onChange={(e) => setCreateData((d) => ({ ...d, description: e.target.value }))}
            />
          </Grid>
        </Grid>
        {isError && <Alert severity="error" style={{ marginTop: 15 }}>{message}</Alert>}
      </DialogContent>
      <DialogActions className={classes.buttonsDialogContainer}>
        <Button onClick={() => setOpenCreate(false)} disabled={isSaving}>Cancelar</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={createDesign}
          disabled={isSaving || !createData.name.trim()}
        >
          Crear
        </Button>
      </DialogActions>
    </Dialog>
  );

  const editDialog = (
    <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
      <DialogTitle>Editar Diseño</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} style={{ marginTop: 4 }}>
          <Grid item xs={12}>
            <TextField
              label="Nombre del diseño"
              name="name"
              fullWidth
              autoFocus
              value={editData.name}
              onChange={(e) => setEditData((d) => ({ ...d, name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descripción (opcional)"
              name="description"
              fullWidth
              multiline
              rows={3}
              value={editData.description}
              onChange={(e) => setEditData((d) => ({ ...d, description: e.target.value }))}
            />
          </Grid>
        </Grid>
        {isError && <Alert severity="error" style={{ marginTop: 15 }}>{message}</Alert>}
      </DialogContent>
      <DialogActions className={classes.buttonsDialogContainer}>
        <Button onClick={() => setOpenEdit(false)} disabled={isSaving}>Cancelar</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={updateDesign}
          disabled={isSaving || !editData.name.trim()}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );

  const deleteDialog = (
    <Dialog open={openDelete} onClose={() => setOpenDelete(false)} aria-labelledby="delete-dialog-title">
      <DialogTitle id="delete-dialog-title">Confirmar eliminación</DialogTitle>
      <DialogContent>
        <Alert severity="warning">
          <p>Se eliminará el diseño y todas sus páginas permanentemente. Esta acción no se puede reversar.</p>
          <p><b>¿Estás seguro que quieres continuar?</b></p>
        </Alert>
        <TextField
          label="Validación"
          helperText="Para eliminar escribe el texto 'eliminar diseño'"
          fullWidth
          className={classes.textValidate}
          value={textValidate}
          onChange={(e) => { setTextValidate(e.target.value); setIsError(false); }}
        />
        {isError && <Alert severity="error">{message}</Alert>}
      </DialogContent>
      <DialogActions className={classes.buttonsDialogContainer}>
        <Button onClick={() => setOpenDelete(false)} disabled={isDeleting}>Cancelar</Button>
        <Button
          color="secondary"
          variant="contained"
          className={classes.deleteButton}
          onClick={deleteDesign}
          disabled={isDeleting || textValidate !== 'eliminar diseño'}
        >
          Sí, eliminar
        </Button>
      </DialogActions>
    </Dialog>
  );


  const assignDialog = (
    <Dialog open={openAssign} onClose={() => setOpenAssign(false)} fullWidth maxWidth="sm">
      <DialogTitle>Asociar a Dominio</DialogTitle>
      <DialogContent>
        {isSiteLoading ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <CircularProgress />
          </div>
        ) : sites.length === 0 ? (
          <Alert severity="info">No tienes sitios activos disponibles para asociar.</Alert>
        ) : (
          <>
            <Alert severity="info" style={{ marginBottom: 12 }}>
              Selecciona el sitio al que deseas asociar este diseño. Un sitio sólo puede tener un diseño asignado.
            </Alert>
            <List dense disablePadding>
              {sites.map((site) => (
                <ListItem
                  key={site.domain}
                  button
                  onClick={() => setAssignSiteId(site.domain)}
                  style={{
                    border: `1px solid ${assignSiteId === site.domain ? '#1976d2' : '#e0e0e0'}`,
                    marginBottom: 6,
                    borderRadius: 4,
                    backgroundColor: assignSiteId === site.domain ? '#e3f2fd' : 'transparent',
                  }}
                >
                  <ListItemText
                    primary={site.name}
                    secondary={
                      <span>
                        {site.domain}
                        {site.designId && site.designId !== assignDesignId && (
                          <span style={{ color: '#ff9800', marginLeft: 8 }}>
                            — ya tiene otro diseño asignado
                          </span>
                        )}
                        {site.designId === assignDesignId && (
                          <span style={{ color: '#4caf50', marginLeft: 8 }}>
                            — ya asociado a este diseño
                          </span>
                        )}
                      </span>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
        {isError && <Alert severity="error" style={{ marginTop: 12 }}>{message}</Alert>}
      </DialogContent>
      <DialogActions className={classes.buttonsDialogContainer}>
        <Button onClick={() => setOpenAssign(false)} disabled={isAssigning}>Cancelar</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={assignDesign}
          disabled={isAssigning || !assignSiteId}
        >
          Asociar
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Grid container spacing={2} className={classes.section} justifyContent="center">
      <Grid item xs={12} sm={12} md={12} lg={12} className={classes.gridMaxMd}>
        <Paper className={classes.paper}>
          <Typography variant="h4" gutterBottom>
            Diseños
          </Typography>

          <div style={{ height: '430px', width: '100%' }}>
            <DataGrid
              getRowId={(row) => row.designId}
              rows={rows}
              columns={columns}
              pageSize={5}
              rowsPerPageOptions={[5]}
              loading={isLoading}
              components={{
                NoRowsOverlay: NoRowsOverlay,
                Toolbar: QuickSearchToolbar,
              }}
              componentsProps={{
                toolbar: {
                  value: searchText,
                  onChange: (e) => requestSearch(e.target.value),
                  clearSearch: () => requestSearch(''),
                  onRefresh: () => fetchDesigns(),
                  addButtonText: 'Nuevo Diseño',
                  onAdd: () => { setCreateData({ name: '', description: '' }); setIsError(false); setOpenCreate(true); },
                },
              }}
              localeText={esES.props.MuiDataGrid.localeText}
            />
          </div>

          {createDialog}
          {editDialog}
          {deleteDialog}
          {assignDialog}
        </Paper>
      </Grid>

      <Snackbar open={openToast} autoHideDuration={6000} onClose={() => setOpenToast(false)}>
        <Alert onClose={() => setOpenToast(false)} severity="success">
          {toastMsg}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default DesignsPage;
